import {Select} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Collapse} from 'src/design/atoms/Collapse';
import {Grid} from 'src/design/atoms/Grid';
import {Switch} from 'src/design/atoms/Switch';
import React, {useMemo} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {RoleEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {ISelectOption} from 'src/types/Form';
import type {InstanceFormValues, InstanceSectionProps} from './SectionProps';
import {ViewField, ViewSwitch} from 'src/design/atoms/ViewField';

const ROLE_OPTIONS = [
  {value: String(RoleEnum.InstanceUser), label: 'Instance User'},
  {value: String(RoleEnum.InstanceObserver), label: 'Instance Observer'},
  {value: String(RoleEnum.InstanceSupervisor), label: 'Instance Supervisor'},
  {
    value: String(RoleEnum.SafetyRepresentative),
    label: 'Safety Representative',
  },
  {value: String(RoleEnum.NotificationOnly), label: 'Notification Only'},
];

const USER_TYPE_OPTIONS = [
  {value: String(UserTypeEnum.Employee), label: 'Employee'},
  {value: String(UserTypeEnum.Contractor), label: 'Contractor'},
];

interface AuthenticationSectionProps extends InstanceSectionProps {
  userGroupOptions: ISelectOption[];
}

export const AuthenticationSection: React.FC<AuthenticationSectionProps> = ({
  mode,
  userGroupOptions,
}) => {
  const {control} = useFormContext<InstanceFormValues>();
  const formData = useWatch({control});
  const isViewMode = mode === 'view';

  const userGroupSelectData = useMemo(
    () =>
      userGroupOptions.map(ug => ({
        value: String(ug.id),
        label: ug.label,
      })),
    [userGroupOptions]
  );

  const resolveRoleLabel = (value: any) =>
    ROLE_OPTIONS.find(o => o.value === String(value))?.label ?? '—';

  const resolveUserTypeLabel = (value: any) =>
    USER_TYPE_OPTIONS.find(o => o.value === String(value))?.label ?? '—';

  const resolveUserGroupLabel = (value: any) =>
    userGroupOptions.find(ug => String(ug.id) === String(value))?.label ?? '—';

  if (isViewMode) {
    return (
      <SectionCard
        title="Authentication & SSO"
        data-testid="section-authentication">
        <Box p="md">
          <Grid gutter="md">
            <Grid.Col span={{base: 12, md: 6}}>
              <ViewField
                label="Multi-factor Authentication"
                value="Users enroll in Security; organization-wide enforcement is unavailable."
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, md: 6}}>
              <ViewSwitch
                label="SSO Self-Registration"
                value={!!formData.ssoSelfRegistrationEnabled}
              />
            </Grid.Col>
            {formData.ssoSelfRegistrationEnabled && (
              <>
                <Grid.Col span={{base: 12, md: 4}}>
                  <ViewField
                    label="Default Role"
                    value={resolveRoleLabel(
                      formData.ssoSelfRegistrationDefaultRole
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 4}}>
                  <ViewField
                    label="Default User Type"
                    value={resolveUserTypeLabel(
                      formData.ssoSelfRegistrationDefaultUserType
                    )}
                  />
                </Grid.Col>
                <Grid.Col span={{base: 12, md: 4}}>
                  <ViewField
                    label="Default User Group"
                    value={resolveUserGroupLabel(
                      formData.ssoSelfRegistrationDefaultUserGroupId
                    )}
                  />
                </Grid.Col>
              </>
            )}
          </Grid>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Authentication & SSO"
      data-testid="section-authentication">
      <Box p="md">
        <Grid gutter="md">
          <Grid.Col span={{base: 12, md: 6}}>
            <Controller
              name="mfaRequired"
              control={control}
              render={({field}) => (
                <Switch
                  data-testid="form-field-mfaRequired"
                  label="Require Multi-factor Authentication"
                  description="Organization-wide enforcement is not available. Users can enable authenticator MFA in Security."
                  disabled
                  color="green.8"
                  checked={!!field.value}
                  onChange={e => field.onChange(e.currentTarget.checked)}
                  mb="xs"
                />
              )}
            />
          </Grid.Col>
        </Grid>

        <Controller
          name="ssoSelfRegistrationEnabled"
          control={control}
          render={({field}) => (
            <Switch
              data-testid="form-field-ssoSelfRegistrationEnabled"
              label="Enable SSO Self-Registration"
              description="Automatically create accounts for users who authenticate via SSO."
              color="green.8"
              checked={!!field.value}
              onChange={e => field.onChange(e.currentTarget.checked)}
              mb="md"
            />
          )}
        />

        <Collapse in={!!formData.ssoSelfRegistrationEnabled}>
          <Box
            pl="md"
            style={{
              borderLeft: '2px solid var(--mantine-color-blue-2)',
            }}>
            <Grid gutter="md">
              <Grid.Col span={{base: 12, md: 4}}>
                <Controller
                  name="ssoSelfRegistrationDefaultRole"
                  control={control}
                  render={({field}) => (
                    <Select
                      data-testid="form-field-ssoSelfRegistrationDefaultRole"
                      label="Default Role"
                      description="Role assigned to auto-created users."
                      value={field.value ? String(field.value) : null}
                      onChange={val => field.onChange(val || '')}
                      onBlur={field.onBlur}
                      data={ROLE_OPTIONS}
                      placeholder="Select default role"
                      comboboxProps={{zIndex: 1001}}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 4}}>
                <Controller
                  name="ssoSelfRegistrationDefaultUserType"
                  control={control}
                  render={({field}) => (
                    <Select
                      data-testid="form-field-ssoSelfRegistrationDefaultUserType"
                      label="Default User Type"
                      description="Affects default group assignment."
                      value={field.value ? String(field.value) : null}
                      onChange={val => field.onChange(val || '')}
                      onBlur={field.onBlur}
                      data={USER_TYPE_OPTIONS}
                      placeholder="Select user type"
                      comboboxProps={{zIndex: 1001}}
                    />
                  )}
                />
              </Grid.Col>
              <Grid.Col span={{base: 12, md: 4}}>
                <Controller
                  name="ssoSelfRegistrationDefaultUserGroupId"
                  control={control}
                  render={({field}) => (
                    <Select
                      data-testid="form-field-ssoSelfRegistrationDefaultUserGroupId"
                      label="Default User Group"
                      description="User group for auto-created users."
                      value={field.value ? String(field.value) : null}
                      onChange={val => field.onChange(val || '')}
                      onBlur={field.onBlur}
                      data={userGroupSelectData}
                      placeholder="Select user group"
                      clearable
                      comboboxProps={{zIndex: 1001}}
                    />
                  )}
                />
              </Grid.Col>
            </Grid>
          </Box>
        </Collapse>
      </Box>
    </SectionCard>
  );
};
